import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Save, Receipt, X, FolderOpen, Layers, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { useSlip } from "../context/SlipContext";
import { useAuth } from "../context/AuthContext";
import { api, toDecimal } from "../lib/api";
import { ProjectedPick } from "./ProjectedPick";
import { TeamLogo, GameLabel } from "./TeamLogo";

export const BetSlip = () => {
  const { legs, open, setOpen, removeLeg, clearLegs } = useSlip();
  const { user } = useAuth();
  const nav = useNavigate();
  const [saved, setSaved] = useState([]);
  const [saving, setSaving] = useState(false);

  const combinedDecimal = legs.reduce((acc, l) => acc * toDecimal(l.odds), 1);
  const stake = 10;
  const payout = legs.length ? (combinedDecimal * stake).toFixed(2) : "0.00";
  const americanParlay =
    combinedDecimal >= 2
      ? `+${Math.round((combinedDecimal - 1) * 100)}`
      : `${Math.round(-100 / (combinedDecimal - 1))}`;

  const loadSaved = () => {
    if (user) api.get("/slips").then((r) => setSaved(r.data)).catch(() => {});
  };
  useEffect(() => {
    if (open) loadSaved();
    // eslint-disable-next-line
  }, [open, user]);

  const save = async () => {
    if (!user) {
      toast.error("Sign in to save your slip");
      setOpen(false);
      nav("/login");
      return;
    }
    if (!legs.length) return;
    setSaving(true);
    try {
      await api.post("/slips", { name: `${legs.length}-Leg Parlay`, legs });
      toast.success("Slip saved to your library");
      loadSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save slip");
    } finally {
      setSaving(false);
    }
  };

  const deleteSaved = async (id) => {
    try {
      await api.delete(`/slips/${id}`);
      setSaved((s) => s.filter((x) => x.id !== id));
    } catch (_) {}
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        data-testid="bet-slip-panel"
        className="bg-[#0B0F17] border-[#233248] text-white w-full sm:max-w-md overflow-y-auto p-0"
      >
        <SheetHeader className="p-5 border-b border-[#233248] glass sticky top-0 z-10">
          <button
            data-testid="slip-back-to-board-button"
            onClick={() => setOpen(false)}
            className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-[#00E5FF] transition-colors mb-2 w-fit"
          >
            <ArrowLeft className="h-4 w-4" /> Back to board
          </button>
          <SheetTitle className="font-heading flex items-center gap-2 text-white">
            <Receipt className="h-5 w-5 text-[#00E5FF]" /> Bet Slip
            <span className="font-data text-sm text-slate-400">({legs.length})</span>
          </SheetTitle>
        </SheetHeader>

        <div className="p-4 space-y-2">
          {legs.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-10">
              Your slip is empty. Add props from the board or AI picks.
            </p>
          )}
          {legs.map((l) => (
            <div
              key={l.prop_id}
              data-testid={`slip-leg-${l.prop_id}`}
              className="flex items-center gap-3 rounded-xl border border-[#233248] bg-[#141C2B] p-3"
            >
              <div className="min-w-0 flex-1">
                <div className="text-white font-semibold text-sm truncate">{l.player_name}</div>
                <div className="font-data text-xs text-slate-400">
                  {l.game_label ? <GameLabel sport={l.sport} label={l.game_label} /> : (
                    <span className="inline-flex items-center gap-1">
                      <TeamLogo sport={l.sport} abbr={l.team_abbr} size="xs" /> vs <TeamLogo sport={l.sport} abbr={l.opponent_abbr} size="xs" />
                    </span>
                  )}
                </div>
                <ProjectedPick
                  testId={`slip-leg-projection-${l.prop_id}`}
                  projection={l.projection}
                  metric={l.metric}
                  side={l.lean}
                  line={l.line}
                  odds={l.odds}
                  book={l.bookmaker}
                  booksAgree={l.books_agree}
                  booksTotal={l.books_total}
                />
              </div>
              <button
                data-testid={`remove-leg-${l.prop_id}`}
                onClick={() => removeLeg(l.prop_id)}
                className="shrink-0 text-slate-500 hover:text-[#EF4444]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>

        {legs.length > 0 && (
          <div className="p-4 space-y-3 border-t border-[#233248] mx-0">
            <div className="rounded-xl bg-[#141C2B] border border-[#233248] p-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400 flex items-center gap-1.5">
                  <Layers className="h-4 w-4" /> {legs.length}-Leg Parlay
                </span>
                <span className="font-data font-bold text-[#00E5FF]">{americanParlay}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">$10 stake pays</span>
                <span className="font-data font-bold text-white">${payout}</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid="clear-slip-button"
                onClick={clearLegs}
                className="rounded-xl border border-[#233248] py-2.5 text-sm text-slate-300 hover:text-white hover:border-[#EF4444]/50"
              >
                Clear
              </button>
              <button
                data-testid="save-slip-button"
                onClick={save}
                disabled={saving}
                className="flex items-center justify-center gap-2 rounded-xl bg-[#00E5FF] text-[#0B0F17] font-semibold py-2.5 text-sm hover:bg-[#33EaFF] disabled:opacity-60"
              >
                <Save className="h-4 w-4" /> Save Slip
              </button>
            </div>
          </div>
        )}

        {user && saved.length > 0 && (
          <div className="p-4 border-t border-[#233248]">
            <div className="flex items-center gap-2 text-sm text-slate-400 mb-2">
              <FolderOpen className="h-4 w-4" /> Saved Slips
            </div>
            <div className="space-y-2" data-testid="saved-slips-list">
              {saved.map((s) => (
                <div key={s.id} className="flex items-center justify-between rounded-lg border border-[#233248] bg-[#141C2B] px-3 py-2">
                  <div>
                    <div className="text-sm text-white">{s.name}</div>
                    <div className="font-data text-[11px] text-slate-500">
                      {new Date(s.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    data-testid={`delete-saved-${s.id}`}
                    onClick={() => deleteSaved(s.id)}
                    className="text-slate-500 hover:text-[#EF4444]"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
